import type { FieldDefinition, FieldsBank, ViewPreset, FieldDataType, FieldOption, FieldSection } from '../interfaces/fields-management';
import { enrichFieldWithLabel } from '../utils/fields/resolveFieldLabels';

// Incoterms del embarque; la plantilla de aduanas de México usa la misma lista.
export const INCOTERM_OPTIONS: FieldOption[] = [
  { id: 'exw', label: 'EXW', value: 'EXW' },
  { id: 'fca', label: 'FCA', value: 'FCA' },
  { id: 'cpt', label: 'CPT', value: 'CPT' },
  { id: 'cip', label: 'CIP', value: 'CIP' },
  { id: 'dap', label: 'DAP', value: 'DAP' },
  { id: 'dpu', label: 'DPU', value: 'DPU' },
  { id: 'ddp', label: 'DDP', value: 'DDP' },
  { id: 'cfr', label: 'CFR', value: 'CFR' },
  { id: 'fob', label: 'FOB', value: 'FOB' },
  { id: 'fas', label: 'FAS', value: 'FAS' },
  { id: 'cif', label: 'CIF', value: 'CIF' }
];

/**
 * Campos del sistema para módulo de Órdenes de Compra
 */
export const purchaseOrdersSystemFields: FieldDefinition[] = [
  // Sección: General
  {
    id: 'po_purchaseOrder',
    name: 'purchaseOrder',
    label: 'Orden de Compra',
    labelES: 'Orden de Compra',
    labelEN: 'Purchase Order',
    type: 'text',
    module: 'purchaseOrders',
    section: 'general',
    required: true,
    visible: true,
    editable: false,
    sortable: true,
    filterable: true,
    searchable: true,
    width: 150,
    order: 1,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'po_supplierName',
    name: 'supplierName',
    label: 'Proveedor',
    labelES: 'Proveedor',
    labelEN: 'Supplier',
    type: 'text',
    module: 'purchaseOrders',
    section: 'general',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: true,
    width: 180,
    order: 2,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'po_status',
    name: 'status',
    label: 'Estado',
    labelES: 'Estado',
    labelEN: 'Status',
    type: 'select',
    module: 'purchaseOrders',
    section: 'general',
    required: true,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 150,
    order: 3,
    options: [
      { id: 'to_be_confirmed', label: 'Por Confirmar', value: 'TO BE CONFIRMED' },
      { id: 'to_be_approved', label: 'Por Aprobar', value: 'TO BE APPROVED' },
      { id: 'approved', label: 'Aprobado', value: 'APPROVED' },
      { id: 'in_transit', label: 'En Tránsito', value: 'IN_TRANSIT' },
      { id: 'delivered', label: 'Entregado', value: 'DELIVERED' },
      { id: 'completed', label: 'Completado', value: 'COMPLETED' },
      { id: 'cancelled', label: 'Cancelado', value: 'CANCELLED' }
    ],
    isCustom: false,
    isSystem: true
  },
  {
    id: 'po_company',
    name: 'company',
    label: 'Empresa',
    labelES: 'Empresa',
    labelEN: 'Company',
    type: 'text',
    module: 'purchaseOrders',
    section: 'general',
    required: false,
    visible: true,
    editable: false,
    sortable: true,
    filterable: true,
    searchable: true,
    width: 120,
    order: 4,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'po_purchaseGroup',
    name: 'purchaseGroup',
    label: 'Grupo de Compra',
    labelES: 'Grupo de Compra',
    labelEN: 'Purchase Group',
    type: 'text',
    module: 'purchaseOrders',
    section: 'general',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: true,
    width: 140,
    order: 5,
    isCustom: false,
    isSystem: true
  },

  // Sección: Logística
  {
    id: 'po_origin',
    name: 'origin',
    label: 'Origen',
    labelES: 'Origen',
    labelEN: 'Origin',
    type: 'text',
    module: 'purchaseOrders',
    section: 'logistics',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: true,
    width: 150,
    order: 10,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'po_destination',
    name: 'destination',
    label: 'Destino',
    labelES: 'Destino',
    labelEN: 'Destination',
    type: 'text',
    module: 'purchaseOrders',
    section: 'logistics',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: true,
    width: 150,
    order: 11,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'po_incoterm',
    name: 'incoterm',
    label: 'Incoterm',
    labelES: 'Incoterm',
    labelEN: 'Incoterm',
    type: 'select',
    module: 'purchaseOrders',
    section: 'logistics',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 120,
    order: 12,
    options: [
      { id: 'exw', label: 'EXW', value: 'EXW' },
      { id: 'fca', label: 'FCA', value: 'FCA' },
      { id: 'fob', label: 'FOB', value: 'FOB' },
      { id: 'cif', label: 'CIF', value: 'CIF' },
      { id: 'ddu', label: 'DDU', value: 'DDU' },
      { id: 'ddp', label: 'DDP', value: 'DDP' }
    ],
    isCustom: false,
    isSystem: true
  },
  {
    id: 'po_packageType',
    name: 'packageType',
    label: 'Tipo de Empaque',
    labelES: 'Tipo de Empaque',
    labelEN: 'Package Type',
    type: 'text',
    module: 'purchaseOrders',
    section: 'logistics',
    required: false,
    visible: false,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: true,
    width: 140,
    order: 13,
    isCustom: false,
    isSystem: true
  },

  // Sección: Fechas
  {
    id: 'po_orderDate',
    name: 'orderDate',
    label: 'Fecha de Orden',
    labelES: 'Fecha de Orden',
    labelEN: 'Order Date',
    type: 'date',
    module: 'purchaseOrders',
    section: 'dates',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 140,
    order: 20,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'po_requestedDate',
    name: 'requestedDate',
    label: 'Fecha Solicitada',
    labelES: 'Fecha Solicitada',
    labelEN: 'Requested Date',
    type: 'date',
    module: 'purchaseOrders',
    section: 'dates',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 150,
    order: 21,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'po_confirmDate',
    name: 'confirmDate',
    label: 'Fecha de Confirmación',
    labelES: 'Fecha de Confirmación',
    labelEN: 'Confirmation Date',
    type: 'date',
    module: 'purchaseOrders',
    section: 'dates',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 170,
    order: 22,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'po_eta',
    name: 'eta',
    label: 'ETA',
    labelES: 'ETA',
    labelEN: 'ETA',
    type: 'date',
    module: 'purchaseOrders',
    section: 'dates',
    required: false,
    visible: false,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 120,
    order: 23,
    isCustom: false,
    isSystem: true
  },

  // Sección: Financiero
  {
    id: 'po_currency',
    name: 'currency',
    label: 'Moneda',
    labelES: 'Moneda',
    labelEN: 'Currency',
    type: 'select',
    module: 'purchaseOrders',
    section: 'financial',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 100,
    order: 30,
    options: [
      { id: 'usd', label: 'USD', value: 'USD' },
      { id: 'eur', label: 'EUR', value: 'EUR' },
      { id: 'mxn', label: 'MXN', value: 'MXN' },
      { id: 'cny', label: 'CNY', value: 'CNY' }
    ],
    isCustom: false,
    isSystem: true
  },
  {
    id: 'po_totalValue',
    name: 'currencyTotalCashExcludingTax',
    label: 'Total sin Impuestos',
    labelES: 'Total sin Impuestos',
    labelEN: 'Total Excluding Tax',
    type: 'currency',
    module: 'purchaseOrders',
    section: 'financial',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: false,
    searchable: false,
    width: 150,
    order: 31,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'po_totalWithTax',
    name: 'currencyTotalCashIncludingTax',
    label: 'Total con Impuestos',
    labelES: 'Total con Impuestos',
    labelEN: 'Total Including Tax',
    type: 'currency',
    module: 'purchaseOrders',
    section: 'financial',
    required: false,
    visible: false,
    editable: true,
    sortable: true,
    filterable: false,
    searchable: false,
    width: 150,
    order: 32,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'po_paymentTerms',
    name: 'paymentTerms',
    label: 'Términos de Pago',
    labelES: 'Términos de Pago',
    labelEN: 'Payment Terms',
    type: 'text',
    module: 'purchaseOrders',
    section: 'financial',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: true,
    searchable: true,
    width: 150,
    order: 33,
    isCustom: false,
    isSystem: true
  }
]

// ============================================
// SHIPMENTS - Campos del Sistema
// ============================================
export const shipmentsSystemFields: FieldDefinition[] = [
  // ====================
  // Sección: Referencias (dashboard.mainInfo.sections.references)
  // ====================
  {
    id: 'ship_customerReference',
    name: 'customerReference',
    label: 'Tipo de Referencia',
    labelES: 'Tipo de Referencia',
    labelEN: 'Reference Type',
    type: 'select',
    module: 'shipments',
    section: 'references',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 180,
    order: 1,
    options: [
      { id: 'delivery_order', label: 'Delivery Order', value: 'Delivery Order' },
      { id: 'purchase_order', label: 'Purchase Order', value: 'Purchase Order' },
      { id: 'quote', label: 'Quote', value: 'Quote' },
      { id: 'warehouse', label: 'Warehouse', value: 'Warehouse' },
      { id: 'pick_up_request', label: 'Pick Up Request', value: 'Pick Up Request' },
      { id: 'other', label: 'Other', value: 'Other' }
    ],
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_company',
    name: 'company',
    label: 'Company',
    labelES: 'Company',
    labelEN: 'Company',
    type: 'text',
    module: 'shipments',
    section: 'references',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 150,
    order: 9,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_consignees',
    name: 'consignees',
    label: 'Consignatario',
    labelES: 'Consignatario',
    labelEN: 'Consignee',
    type: 'text',
    module: 'shipments',
    section: 'references',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 180,
    order: 10,
    isCustom: false,
    isSystem: true
  },

  // ====================
  // Sección: Configuración de Transporte (dashboard.mainInfo.sections.transportConfig)
  // ====================
  {
    id: 'ship_transportMode',
    name: 'transportMode',
    label: 'Modo de Transporte',
    labelES: 'Modo de Transporte',
    labelEN: 'Transport Mode',
    type: 'select',
    module: 'shipments',
    section: 'transportConfig',
    required: true,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 160,
    order: 10,
    options: [
      { id: 'air', label: 'Air Transport', value: 'Air Transport' },
      { id: 'ocean', label: 'Ocean Transport', value: 'Ocean Transport' },
      { id: 'ground', label: 'Ground Transport', value: 'Ground Transport' }
    ],
    isCustom: false,
    isSystem: true
  },
  
  {
    id: 'ship_shipmentType',
    name: 'shipmentType',
    label: 'Tipo de Embarque',
    labelES: 'Tipo de Embarque',
    labelEN: 'Shipment Type',
    type: 'select',
    module: 'shipments',
    section: 'transportConfig',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 140,
    order: 11,
    options: [
      { id: 'FCL', label: 'FCL', value: 'FCL' },
      { id: 'LCL', label: 'LCL', value: 'LCL' },
      { id: 'FTL', label: 'FTL', value: 'FTL' },
      { id: 'LTL', label: 'LTL', value: 'LTL' },
      { id: 'BBK', label: 'Break Bulk', value: 'BBK' },
      { id: 'BLK', label: 'Bulk', value: 'BLK' },
      { id: 'BCN', label: "Buyer's Consolidation", value: 'BCN' },
      { id: 'CON', label: 'Agent Consolidation', value: 'CON' },
      { id: 'LQD', label: 'Liquid', value: 'LQD' },
      { id: 'LSE', label: 'Loose', value: 'LSE' },
      { id: 'OBC', label: 'On Board Courier', value: 'OBC' },
      { id: 'ROR', label: 'Roll On/Roll Off', value: 'ROR' },
      { id: 'ULD', label: 'Unit Load Device', value: 'ULD' },
      { id: 'UNA', label: 'Unaccompanied', value: 'UNA' },
    ],
    isCustom: false,
    isSystem: true
  },

  // ====================
  // Sección: Incoterms (dashboard.mainInfo.sections.incoterm)
  // ====================
  {
    id: 'ship_incoterm',
    name: 'incoterm',
    label: 'Incoterm',
    labelES: 'Incoterm',
    labelEN: 'Incoterm',
    type: 'select',
    module: 'shipments',
    section: 'incoterm',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 120,
    order: 11,
    options: INCOTERM_OPTIONS,
    isCustom: false,
    isSystem: true
  },
  
  // ====================
  // Sección: Documentos de Transporte (dashboard.mainInfo.sections.transportDocs)
  // ====================
  {
    id: 'ship_booking',
    name: 'booking',
    label: 'Booking',
    labelES: 'Booking',
    labelEN: 'Booking',
    type: 'text',
    module: 'shipments',
    section: 'transportDocs',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 150,
    order: 12,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_mawb',
    name: 'mawb',
    label: 'MAWB',
    labelES: 'MAWB',
    labelEN: 'MAWB',
    type: 'text',
    module: 'shipments',
    section: 'transportDocs',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 150,
    order: 13,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_hawb',
    name: 'hawb',
    label: 'HAWB',
    labelES: 'HAWB',
    labelEN: 'HAWB',
    type: 'text',
    module: 'shipments',
    section: 'transportDocs',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 150,
    order: 14,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_mbl',
    name: 'mbl',
    label: 'MBL',
    labelES: 'MBL',
    labelEN: 'MBL',
    type: 'text',
    module: 'shipments',
    section: 'transportDocs',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 150,
    order: 15,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_hbl',
    name: 'hbl',
    label: 'HBL',
    labelES: 'HBL',
    labelEN: 'HBL',
    type: 'text',
    module: 'shipments',
    section: 'transportDocs',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 150,
    order: 16,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_containerNumber',
    name: 'containerNumber',
    label: 'Container Number',
    labelES: 'Container Number',
    labelEN: 'Container Number',
    type: 'text',
    module: 'shipments',
    section: 'transportDocs',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 150,
    order: 16.5,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_shipmentDate',
    name: 'shipmentDate',
    label: 'Fecha de Embarque',
    labelES: 'Fecha de Embarque',
    labelEN: 'Shipment Date',
    type: 'date',
    module: 'shipments',
    section: 'transportDocs',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 160,
    order: 17,
    isCustom: false,
    isSystem: true
  },
  
  // ====================
  // Sección: Transportistas (dashboard.mainInfo.sections.carriers)
  // ====================
  {
    id: 'ship_pl3',
    name: 'pl3',
    label: '3PL',
    labelES: '3PL',
    labelEN: '3PL',
    type: 'select',
    module: 'shipments',
    section: 'carriers',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 150,
    order: 18,
    isCustom: false,
    isSystem: true,
    catalogSource: {
      catalog: 'INTERNATIONAL_CARRIER',
      filter: {
        field: 'type',
        value: 'forwarder'
      }
    }
  },
  {
    id: 'ship_internationalCarrier',
    name: 'internationalCarrier',
    label: 'Transportista Internacional',
    labelES: 'Transportista Internacional',
    labelEN: 'International Carrier',
    type: 'select',
    module: 'shipments',
    section: 'carriers',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 180,
    order: 19,
    isCustom: false,
    isSystem: true,
    catalogSource: {
      catalog: 'INTERNATIONAL_CARRIER',
      filter: {
        field: 'type',
        value: 'carrier'
      }
    }
  },
  {
    id: 'ship_freeDays',
    name: 'freeDays',
    label: 'Días Libres',
    labelES: 'Días Libres',
    labelEN: 'Free Days',
    type: 'number',
    module: 'shipments',
    section: 'carriers',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 120,
    order: 20,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_initialFreight',
    name: 'initialFreight',
    label: 'Flete Inicial',
    labelES: 'Flete Inicial',
    labelEN: 'Initial Freight',
    type: 'currency',
    module: 'shipments',
    section: 'carriers',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 140,
    order: 21,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_finalFreight',
    name: 'finalFreight',
    label: 'Flete Final',
    labelES: 'Flete Final',
    labelEN: 'Final Freight',
    type: 'currency',
    module: 'shipments',
    section: 'carriers',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 140,
    order: 22,
    isCustom: false,
    isSystem: true
  },
  
  // ====================
  // Sección: Aduanas - Detalle (customsDetail)
  // ====================
  {
    id: 'ship_drfi_number',
    name: 'drfi_number',
    label: 'DRFI - Número',
    labelES: 'DRFI - Número',
    labelEN: 'DRFI - Number',
    type: 'text',
    module: 'shipments',
    section: 'customsDetail',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 140,
    order: 100,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_drfi_expiration_date',
    name: 'drfi_expiration_date',
    label: 'DRFI - Fecha Vencimiento',
    labelES: 'DRFI - Fecha Vencimiento',
    labelEN: 'DRFI - Expiration Date',
    type: 'date',
    module: 'shipments',
    section: 'customsDetail',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 160,
    order: 101,
    isCustom: false,
    isSystem: true
  },
  
  // ====================
  // Sección: Hitos - Fechas Solicitadas (dashboard.milestones.sections.requestedDates)
  // ====================
  {
    id: 'ship_requestedPickupDate',
    name: 'requestedPickupDate',
    label: 'Fecha Solicitada Recogida',
    labelES: 'Fecha Solicitada Recogida',
    labelEN: 'Requested Pickup Date',
    type: 'date',
    module: 'shipments',
    section: 'requestedDates',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: true,
    searchable: false,
    width: 190,
    order: 30,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_requestedDeliveryDate',
    name: 'requestedDeliveryDate',
    label: 'Fecha Solicitada Entrega',
    labelES: 'Fecha Solicitada Entrega',
    labelEN: 'Requested Delivery Date',
    type: 'date',
    module: 'shipments',
    section: 'requestedDates',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: true,
    searchable: false,
    width: 190,
    order: 31,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_requestedTransitTime',
    name: 'requestedTransitTime',
    label: 'Tiempo de Tránsito Solicitado',
    labelES: 'Tiempo de Tránsito Solicitado',
    labelEN: 'Requested Transit Time',
    type: 'number',
    module: 'shipments',
    section: 'requestedDates',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: true,
    searchable: false,
    width: 190,
    order: 32,
    isCustom: false,
    isSystem: true
  },
  
  // ====================
  // Sección: Hitos - Tiempos Estimados (dashboard.milestones.sections.estimatedTimes)
  // ====================
  {
    id: 'ship_estimatedPickupDate',
    name: 'estimatedPickupDate',
    label: 'Estimated Pickup Date',
    labelES: 'Estimated Pickup Date',
    labelEN: 'Estimated Pickup Date',
    type: 'date',
    module: 'shipments',
    section: 'estimatedTimes',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: true,
    searchable: false,
    width: 180,
    order: 39,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_etd',
    name: 'etd',
    label: 'ETD - Estimated Time of Departure',
    labelES: 'ETD - Estimated Time of Departure',
    labelEN: 'ETD - Estimated Time of Departure',
    type: 'date',
    module: 'shipments',
    section: 'estimatedTimes',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 160,
    order: 40,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_eta',
    name: 'eta',
    label: 'ETA - Estimated Time of Arrival',
    labelES: 'ETA - Estimated Time of Arrival',
    labelEN: 'ETA - Estimated Time of Arrival',
    type: 'date',
    module: 'shipments',
    section: 'estimatedTimes',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 160,
    order: 41,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_firstEta',
    name: 'firstEta',
    label: 'Primer ETA',
    labelES: 'Primer ETA',
    labelEN: 'First ETA',
    type: 'date',
    module: 'shipments',
    section: 'estimatedTimes',
    required: false,
    visible: false,
    editable: false,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 160,
    order: 42,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_firstEtd',
    name: 'firstEtd',
    label: 'Primer ETD',
    labelES: 'Primer ETD',
    labelEN: 'First ETD',
    type: 'date',
    module: 'shipments',
    section: 'estimatedTimes',
    required: false,
    visible: false,
    editable: false,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 160,
    order: 43,
    isCustom: false,
    isSystem: true
  },

  // ====================
  // Sección: Hitos - Eventos Actuales (dashboard.milestones.sections.actualEvents)
  // ====================
  {
    id: 'ship_atd',
    name: 'atd',
    label: 'ATD - Actual Time of Departure',
    labelES: 'ATD - Actual Time of Departure',
    labelEN: 'ATD - Actual Time of Departure',
    type: 'date',
    module: 'shipments',
    section: 'actualEvents',
    required: false,
    visible: false,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 160,
    order: 50,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_ata',
    name: 'ata',
    label: 'ATA - Actual Time of Arrival',
    labelES: 'ATA - Actual Time of Arrival',
    labelEN: 'ATA - Actual Time of Arrival',
    type: 'date',
    module: 'shipments',
    section: 'actualEvents',
    required: false,
    visible: false,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 160,
    order: 51,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_pickupDate',
    name: 'pickupDate',
    label: 'Fecha de Recogida',
    labelES: 'Fecha de Recogida',
    labelEN: 'Pickup Date',
    type: 'date',
    module: 'shipments',
    section: 'actualEvents',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: true,
    searchable: false,
    width: 160,
    order: 52,
    isCustom: false,
    isSystem: true
  },
  
  // ====================
  // Sección: Información Adicional (additionalInfo) - Campos extra no en secciones principales
  // ====================
  {
    id: 'ship_idNumber',
    name: 'idNumber',
    label: 'ID Number',
    labelES: 'ID Number',
    labelEN: 'ID Number',
    type: 'text',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: true,
    width: 150,
    order: 60,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_status',
    name: 'status',
    label: 'Estado del Embarque',
    labelES: 'Estado del Embarque',
    labelEN: 'Shipment Status',
    type: 'select',
    module: 'shipments',
    section: 'additionalInfo',
    required: true,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 150,
    order: 61,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_origin',
    name: 'origin',
    label: 'Origen',
    labelES: 'Origen',
    labelEN: 'Origin',
    type: 'text',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: true,
    width: 150,
    order: 62,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_destination',
    name: 'destination',
    label: 'Destino',
    labelES: 'Destino',
    labelEN: 'Destination',
    type: 'text',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: true,
    width: 150,
    order: 63,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_co2',
    name: 'co2',
    label: 'CO2 Emissions',
    labelES: 'CO2 Emissions',
    labelEN: 'CO2 Emissions',
    type: 'text',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: true,
    editable: false,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 120,
    order: 64,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_createdBy',
    name: 'createdBy',
    label: 'Creado Por',
    labelES: 'Creado Por',
    labelEN: 'Created By',
    type: 'text',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: true,
    editable: false,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 150,
    order: 65,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_assignedTo',
    name: 'assignedTo',
    label: 'Asignado A',
    labelES: 'Asignado A',
    labelEN: 'Assigned To',
    type: 'text',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: false,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 150,
    order: 66,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_createdAt',
    name: 'createdAt',
    label: 'Fecha de Creación',
    labelES: 'Fecha de Creación',
    labelEN: 'Creation Date',
    type: 'datetime',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: true,
    editable: false,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 160,
    order: 67,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_lastUpdate',
    name: 'lastUpdate',
    label: 'Última Actualización',
    labelES: 'Última Actualización',
    labelEN: 'Last Update',
    type: 'datetime',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: false,
    editable: false,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 160,
    order: 68,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_purchaseOrder',
    name: 'purchaseOrder',
    label: 'Orden de Compra',
    labelES: 'Orden de Compra',
    labelEN: 'Purchase Order',
    type: 'text',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 180,
    order: 68.1,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_invoice',
    name: 'invoice',
    label: 'Factura',
    labelES: 'Factura',
    labelEN: 'Invoice',
    type: 'text',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 150,
    order: 68.2,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_priority',
    name: 'priority',
    label: 'Prioridad',
    labelES: 'Prioridad',
    labelEN: 'Priority',
    type: 'select',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 120,
    order: 68.3,
    options: [
      { id: 'low', label: 'Baja', value: 'low' },
      { id: 'medium', label: 'Media', value: 'medium' },
      { id: 'high', label: 'Alta', value: 'high' },
      { id: 'urgent', label: 'Urgente', value: 'urgent' }
    ],
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_additionalComments',
    name: 'additionalComments',
    label: 'Comentarios Adicionales',
    labelES: 'Comentarios Adicionales',
    labelEN: 'Additional Comments',
    type: 'textarea',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 200,
    order: 69,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_leadTimePTP_ATA',
    name: 'leadTimePTP_ATA',
    label: 'Lead Time PTP to ATA',
    labelES: 'Lead Time PTP to ATA',
    labelEN: 'Lead Time PTP to ATA',
    type: 'text',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: false,
    editable: false,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 150,
    order: 70,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_portOfReceipt',
    name: 'portOfReceipt',
    label: 'Puerto de Recibo',
    labelES: 'Puerto de Recibo',
    labelEN: 'Port of Receipt',
    type: 'text',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 150,
    order: 71,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_portOfLoading',
    name: 'portOfLoading',
    label: 'Puerto de Carga',
    labelES: 'Puerto de Carga',
    labelEN: 'Port of Loading',
    type: 'text',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 150,
    order: 72,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_portOfDischarge',
    name: 'portOfDischarge',
    label: 'Puerto de Descarga',
    labelES: 'Puerto de Descarga',
    labelEN: 'Port of Discharge',
    type: 'text',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 150,
    order: 73,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_lastMileCarrier',
    name: 'lastMileCarrier',
    label: 'Transportista Última Milla',
    labelES: 'Transportista Última Milla',
    labelEN: 'Last Mile Carrier',
    type: 'text',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 180,
    order: 74,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_vessel',
    name: 'vessel',
    label: 'Buque/Vuelo',
    labelES: 'Buque/Vuelo',
    labelEN: 'Vessel/Flight',
    type: 'text',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 150,
    order: 75,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_voyage',
    name: 'voyage',
    label: 'Viaje',
    labelES: 'Viaje',
    labelEN: 'Voyage',
    type: 'text',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 120,
    order: 76,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_flag',
    name: 'flag',
    label: 'Bandera',
    labelES: 'Bandera',
    labelEN: 'Flag',
    type: 'select',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: true,
    searchable: false,
    width: 120,
    order: 77,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_lastMilePickupLocation',
    name: 'lastMilePickupLocation',
    label: 'Ubicación Recogida Última Milla',
    labelES: 'Ubicación Recogida Última Milla',
    labelEN: 'Last Mile Pickup Location',
    type: 'text',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 200,
    order: 78,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_lastMileDeliveryPlace',
    name: 'lastMileDeliveryPlace',
    label: 'Lugar Entrega Última Milla',
    labelES: 'Lugar Entrega Última Milla',
    labelEN: 'Last Mile Delivery Place',
    type: 'text',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 200,
    order: 79,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_deliveryDate',
    name: 'deliveryDate',
    label: 'Delivery Date',
    labelES: 'Delivery Date',
    labelEN: 'Delivery Date',
    type: 'date',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: false,
    editable: false,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 150,
    order: 80,
    isCustom: false,
    isSystem: true
  },
  // Desglose Factura Comercial en USD (calculado desde los cargos de terceros, solo lectura)
  {
    id: 'ship_fobUsd',
    name: 'fobUsd',
    label: 'FOB USD',
    labelES: 'FOB USD',
    labelEN: 'FOB USD',
    type: 'currency',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: true,
    editable: false,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 140,
    order: 91,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_freightUsd',
    name: 'freightUsd',
    label: 'Flete USD',
    labelES: 'Flete USD',
    labelEN: 'Freight USD',
    type: 'currency',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: true,
    editable: false,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 140,
    order: 92,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_insuranceUsd',
    name: 'insuranceUsd',
    label: 'Seguro USD',
    labelES: 'Seguro USD',
    labelEN: 'Insurance USD',
    type: 'currency',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: true,
    editable: false,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 140,
    order: 93,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_cifUsd',
    name: 'cifUsd',
    label: 'Valor CIF USD',
    labelES: 'Valor CIF USD',
    labelEN: 'CIF Value USD',
    type: 'currency',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: true,
    editable: false,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 140,
    order: 94,
    isCustom: false,
    isSystem: true
  },
  // Desglose Factura Comercial en moneda local: lo calcula el tab de Cargos, pero
  // admite valor manual. Editarlo lo marca en `_manualOverrides` y el desglose deja
  // de recalcularlo (ver @/utils/fields/calculatedFieldOverride).
  {
    id: 'ship_fobLocal',
    name: 'fobLocal',
    label: 'FOB Moneda Local',
    labelES: 'FOB Moneda Local',
    labelEN: 'FOB Local Currency',
    type: 'currency',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 140,
    order: 95,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_freightLocal',
    name: 'freightLocal',
    label: 'Flete Moneda Local',
    labelES: 'Flete Moneda Local',
    labelEN: 'Freight Local Currency',
    type: 'currency',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 140,
    order: 96,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_insuranceLocal',
    name: 'insuranceLocal',
    label: 'Seguro Moneda Local',
    labelES: 'Seguro Moneda Local',
    labelEN: 'Insurance Local Currency',
    type: 'currency',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 140,
    order: 97,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_cifLocal',
    name: 'cifLocal',
    label: 'Valor CIF Moneda Local',
    labelES: 'Valor CIF Moneda Local',
    labelEN: 'CIF Value Local Currency',
    type: 'currency',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 140,
    order: 98,
    isCustom: false,
    isSystem: true
  },
  // Suma de todos los cargos del embarque en moneda local. La escribe el backend
  // ante cualquier cambio en la subcolección `charges`; el tab de Cargos la
  // adelanta localmente para no esperar al trigger.
  {
    id: 'ship_totalCharges',
    name: 'totalCharges',
    label: 'Total de Cargos',
    labelES: 'Total de Cargos',
    labelEN: 'Total Charges',
    type: 'currency',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: true,
    editable: false,
    sortable: false,
    filterable: false,
    searchable: false,
    width: 140,
    order: 99,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'ship_policyNumber',
    name: 'policyNumber',
    label: 'Póliza de Seguro',
    labelES: 'Póliza de Seguro',
    labelEN: 'Insurance Policy Number',
    type: 'text',
    module: 'shipments',
    section: 'additionalInfo',
    required: false,
    visible: true,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 180,
    order: 99,
    isCustom: false,
    isSystem: true
  },
  // ============================================
  // Fechas del contenedor (containerField). Las compañías creadas antes de que
  // existieran no las tienen en su config y no salían en estados ni plantillas.
  ...([
    { id: 'loading_appointment_date', name: 'loadingAppointmentDate', labelES: 'Cita de cargue', labelEN: 'Loading Appointment Date', order: 40, width: 180 },
    { id: 'unloading_date', name: 'unloadingDate', labelES: 'Fecha de descargue', labelEN: 'Unloading Date', order: 60, width: 160 },
    { id: 'port_return_date', name: 'portReturnDate', labelES: 'Fecha de entrega de unidad vacía', labelEN: 'Empty Unit Return Date', order: 60, width: 160 }
  ]).map(f => ({
    ...f,
    label: f.labelES,
    type: 'date' as const,
    module: 'shipments' as const,
    section: 'tableOnly' as const,
    containerField: true,
    required: false,
    visible: false,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    isCustom: false,
    isSystem: true
  })),
  // ============================================
  // Sección PACKAGES - Columnas de la tabla de paquetes (subcolección packages)
  // El name es la clave real del documento de la subcolección, salvo
  // pack_quantity/pack_type que evitan colisionar con names de otras secciones
  // del módulo (items.quantity, tableOnly.container_type): sus claves reales
  // son quantity/type (quitar el prefijo pack_).
  ...([
    { name: 'pack_quantity', id: 'packages_quantity', labelES: 'Cantidad', labelEN: 'Quantity', type: 'number', required: true, visible: true },
    { name: 'pack_type', id: 'packages_type', labelES: 'Tipo de paquete', labelEN: 'Package Type', type: 'text', required: true, visible: true },
    { name: 'reference', labelES: 'ID de Referencia', labelEN: 'ID Reference', type: 'text', visible: true },
    { name: 'length', labelES: 'Largo', labelEN: 'Length', type: 'number', visible: true },
    { name: 'width', labelES: 'Ancho', labelEN: 'Width', type: 'number', visible: true },
    { name: 'height', labelES: 'Alto', labelEN: 'Height', type: 'number', visible: true },
    { name: 'dimensionsUoM', labelES: 'Unidad de dimensiones', labelEN: 'Dimensions UoM', type: 'text' },
    { name: 'totalVolume', labelES: 'Volumen total', labelEN: 'Total Volume', type: 'number', visible: true },
    { name: 'volumeUoM', labelES: 'Unidad de volumen', labelEN: 'Volume UoM', type: 'text' },
    { name: 'unitWeight', labelES: 'Peso unitario', labelEN: 'Unit Weight', type: 'number', visible: true },
    { name: 'totalWeight', labelES: 'Peso total', labelEN: 'Total Weight', type: 'number', visible: true },
    { name: 'weightUoM', labelES: 'Unidad de peso', labelEN: 'Weight UoM', type: 'text' },
    { name: 'volumetricWeight', labelES: 'Peso volumétrico', labelEN: 'Volumetric Weight', type: 'number' },
    { name: 'chargeableWeight', labelES: 'Peso tarifado', labelEN: 'Chargeable Weight', type: 'number' },
    { name: 'requiresTemperature', labelES: 'Requiere control de temperatura', labelEN: 'Requires Temperature Control', type: 'boolean' },
    { name: 'minTemp', labelES: 'Temperatura mínima', labelEN: 'Minimum Temperature', type: 'number' },
    { name: 'maxTemp', labelES: 'Temperatura máxima', labelEN: 'Maximum Temperature', type: 'number' },
    { name: 'tempUoM', labelES: 'Unidad de temperatura', labelEN: 'Temperature UoM', type: 'text' },
    { name: 'isHazardous', labelES: 'Es mercancía peligrosa', labelEN: 'Is Hazardous Material', type: 'boolean' },
    { name: 'undg', labelES: 'UNDG', labelEN: 'UNDG Number', type: 'text' },
    { name: 'imoCode', labelES: 'Código IMO', labelEN: 'IMO Class Code', type: 'text' },
    { name: 'flashpoint', labelES: 'Punto de inflamación', labelEN: 'Flash Point', type: 'number' },
    { name: 'flashpointUoM', labelES: 'Unidad de punto de inflamación', labelEN: 'Flash Point UoM', type: 'text' }
  ] as Array<{ name: string; id?: string; labelES: string; labelEN: string; type: FieldDataType; required?: boolean; visible?: boolean }>).map((f, i) => ({
    id: f.id || `packages_${f.name}`,
    name: f.name,
    label: f.labelES,
    labelES: f.labelES,
    labelEN: f.labelEN,
    type: f.type,
    module: 'shipments' as const,
    section: 'packages' as const,
    required: f.required ?? false,
    visible: f.visible ?? false,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: f.type === 'text',
    isCustom: false,
    isSystem: true,
    order: i + 1
  }))
]

export const shipmentsCustomFields: FieldDefinition[] = []

// ============================================
// QUOTES - Campos del Sistema
// ============================================
export const quotesSystemFields: FieldDefinition[] = [
  // Sección: General
  {
    id: 'quote_quoteNumber',
    name: 'quoteNumber',
    label: 'Número de Cotización',
    labelES: 'Número de Cotización',
    labelEN: 'Quote Number',
    type: 'text',
    module: 'quotes',
    section: 'general',
    required: true,
    visible: true,
    editable: false,
    sortable: true,
    filterable: true,
    searchable: true,
    width: 180,
    order: 1,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'quote_status',
    name: 'status',
    label: 'Estado',
    labelES: 'Estado',
    labelEN: 'Status',
    type: 'select',
    module: 'quotes',
    section: 'general',
    required: true,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 140,
    order: 2,
    options: [
      { id: 'draft', label: 'Borrador', value: 'draft' },
      { id: 'sent', label: 'Enviada', value: 'sent' },
      { id: 'accepted', label: 'Aceptada', value: 'accepted' },
      { id: 'rejected', label: 'Rechazada', value: 'rejected' },
      { id: 'expired', label: 'Expirada', value: 'expired' }
    ],
    isCustom: false,
    isSystem: true
  },
  {
    id: 'quote_customerName',
    name: 'customerName',
    label: 'Cliente',
    labelES: 'Cliente',
    labelEN: 'Client',
    type: 'text',
    module: 'quotes',
    section: 'general',
    required: true,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: true,
    width: 180,
    order: 3,
    isCustom: false,
    isSystem: true
  },

  // Sección: Logística
  {
    id: 'quote_origin',
    name: 'origin',
    label: 'Origen',
    labelES: 'Origen',
    labelEN: 'Origin',
    type: 'text',
    module: 'quotes',
    section: 'logistics',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: true,
    width: 150,
    order: 10,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'quote_destination',
    name: 'destination',
    label: 'Destino',
    labelES: 'Destino',
    labelEN: 'Destination',
    type: 'text',
    module: 'quotes',
    section: 'logistics',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: true,
    width: 150,
    order: 11,
    isCustom: false,
    isSystem: true
  },

  // Sección: Fechas
  {
    id: 'quote_quoteDate',
    name: 'quoteDate',
    label: 'Fecha de Cotización',
    labelES: 'Fecha de Cotización',
    labelEN: 'Quote Date',
    type: 'date',
    module: 'quotes',
    section: 'dates',
    required: true,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 170,
    order: 20,
    isCustom: false,
    isSystem: true
  },
  {
    id: 'quote_validUntil',
    name: 'validUntil',
    label: 'Válida Hasta',
    labelES: 'Válida Hasta',
    labelEN: 'Valid Until',
    type: 'date',
    module: 'quotes',
    section: 'dates',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 140,
    order: 21,
    isCustom: false,
    isSystem: true
  },

  // Sección: Financiero
  {
    id: 'quote_currency',
    name: 'currency',
    label: 'Moneda',
    labelES: 'Moneda',
    labelEN: 'Currency',
    type: 'select',
    module: 'quotes',
    section: 'financial',
    required: true,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 100,
    order: 30,
    options: [
      { id: 'usd', label: 'USD', value: 'USD' },
      { id: 'eur', label: 'EUR', value: 'EUR' },
      { id: 'mxn', label: 'MXN', value: 'MXN' }
    ],
    isCustom: false,
    isSystem: true
  },
  {
    id: 'quote_totalAmount',
    name: 'totalAmount',
    label: 'Monto Total',
    labelES: 'Monto Total',
    labelEN: 'Total Amount',
    type: 'currency',
    module: 'quotes',
    section: 'financial',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 150,
    order: 31,
    isCustom: false,
    isSystem: true
  }
]

export const quotesCustomFields: FieldDefinition[] = []

/**
 * Campos personalizados de ejemplo
 */
export const purchaseOrdersCustomFields: FieldDefinition[] = [
  {
    id: 'po_custom_priority',
    name: 'priority',
    label: 'Prioridad',
    labelES: 'Prioridad',
    labelEN: 'Priority',
    type: 'select',
    module: 'purchaseOrders',
    section: 'custom',
    required: false,
    visible: true,
    editable: true,
    sortable: true,
    filterable: true,
    searchable: false,
    width: 120,
    order: 100,
    options: [
      { id: 'low', label: 'Baja', value: 'low', color: 'gray' },
      { id: 'medium', label: 'Media', value: 'medium', color: 'yellow' },
      { id: 'high', label: 'Alta', value: 'high', color: 'orange' },
      { id: 'urgent', label: 'Urgente', value: 'urgent', color: 'red' }
    ],
    isCustom: true,
    isSystem: false,
    createdAt: '2024-01-15T10:00:00Z',
    createdBy: 'user123'
  },
  {
    id: 'po_custom_notes',
    name: 'internalNotes',
    label: 'Notas Internas',
    labelES: 'Notas Internas',
    labelEN: 'Internal Notes',
    type: 'textarea',
    module: 'purchaseOrders',
    section: 'custom',
    required: false,
    visible: false,
    editable: true,
    sortable: false,
    filterable: false,
    searchable: true,
    width: 200,
    order: 101,
    placeholder: 'Agregar notas internas...',
    helpText: 'Estas notas solo son visibles internamente',
    isCustom: true,
    isSystem: false,
    createdAt: '2024-01-20T14:30:00Z',
    createdBy: 'user456'
  }
]

// ===================================
// WAREHOUSE (recibos de almacén)
// ===================================

/**
 * Secciones del detalle del recibo, en el orden en que aparecen. La clave i18n
 * es la que ya usa la página de warehouse para cada bloque.
 */
export const warehouseSections: { value: FieldSection; labelKey: string }[] = [
  { value: 'receipt', labelKey: 'warehouseReceipts.sections.receipt' },
  { value: 'receiptReferences', labelKey: 'warehouseReceipts.infoCards.references' },
  { value: 'supplier', labelKey: 'warehouseReceipts.sections.supplier' },
  { value: 'location', labelKey: 'warehouseReceipts.sections.location' },
  { value: 'shippingParties', labelKey: 'warehouseReceipts.sections.shippingParties' },
  { value: 'additionalInfo', labelKey: 'warehouseReceipts.sections.additionalInfo' }
]

let warehouseFieldOrder = 0
const warehouseField = (
  name: string,
  labelES: string,
  labelEN: string,
  section: FieldSection,
  opts: Partial<FieldDefinition> = {}
): FieldDefinition => ({
  id: `wh_${name}`,
  name,
  label: labelES,
  labelES,
  labelEN,
  type: 'text',
  module: 'warehouse',
  section,
  required: false,
  visible: true,
  editable: true,
  sortable: true,
  filterable: true,
  searchable: true,
  width: 150,
  order: ++warehouseFieldOrder,
  isCustom: false,
  isSystem: true,
  ...opts
})

// required: true = no se puede ocultar (FieldsConfigurationModal y Settings).
// Son los que sostienen el estado (incluida la anulación), el vínculo con el
// embarque y el identificador del recibo.
const whLocked = { required: true, editable: false }

/**
 * Campos del sistema del recibo (WarehouseReceipt en src/types/warehouse.ts).
 * Los de información adicional arrancan ocultos porque el detalle no los
 * mostraba antes; el administrador los agrega desde "Editar campos".
 */
export const warehouseSystemFields: FieldDefinition[] = [
  warehouseField('internalwhId', 'ID Warehouse', 'Warehouse ID', 'receipt', whLocked),
  warehouseField('status', 'Estado', 'Status', 'receipt', whLocked),
  warehouseField('shipmentNumber', 'Embarque', 'Shipment', 'receipt', whLocked),
  warehouseField('shipmentId', 'ID del embarque', 'Shipment ID', 'receipt', whLocked),
  warehouseField('createdAt', 'Fecha de creación', 'Created at', 'receipt', { type: 'date', editable: false }),
  warehouseField('createdByName', 'Creado por', 'Created by', 'receipt', { editable: false }),
  warehouseField('daysOnHand', 'Días en bodega', 'Days on hand', 'receipt', { type: 'number', editable: false }),
  warehouseField('receiptNumber', 'Número de recibo', 'Receipt number', 'receiptReferences'),
  warehouseField('supplierInvoiceNumber', 'Factura del proveedor', 'Supplier invoice', 'receiptReferences'),
  warehouseField('supplierPONumber', 'OC del proveedor', 'Supplier PO', 'receiptReferences'),
  warehouseField('supplierName', 'Proveedor', 'Supplier', 'supplier'),
  warehouseField('supplierAddress', 'Dirección del proveedor', 'Supplier address', 'supplier'),
  warehouseField('originCountryName', 'Origen', 'Origin', 'location'),
  warehouseField('destinationCountryName', 'Destino', 'Destination', 'location'),
  warehouseField('shipperName', 'Remitente', 'Shipper', 'shippingParties'),
  warehouseField('shipperAddress', 'Dirección del remitente', 'Shipper address', 'shippingParties'),
  warehouseField('consigneeName', 'Consignatario', 'Consignee', 'shippingParties'),
  warehouseField('consigneeAddress', 'Dirección del consignatario', 'Consignee address', 'shippingParties'),
  warehouseField('carrierName', 'Transportista', 'Carrier', 'shippingParties'),
  warehouseField('carrierTrackingNumber', 'Guía del transportista', 'Carrier tracking number', 'shippingParties'),
  warehouseField('totalValue', 'Valor total', 'Total value', 'additionalInfo', { type: 'number', visible: false }),
  warehouseField('incomingModeofTransportation', 'Modo de transporte de entrada', 'Incoming mode of transportation', 'additionalInfo', { visible: false }),
  warehouseField('incomingWaybillNumber', 'Guía de entrada', 'Incoming waybill number', 'additionalInfo', { visible: false }),
  warehouseField('incomingDepartureDate', 'Fecha de salida de entrada', 'Incoming departure date', 'additionalInfo', { type: 'date', visible: false }),
  warehouseField('incomingArrivalDate', 'Fecha de llegada de entrada', 'Incoming arrival date', 'additionalInfo', { type: 'date', visible: false }),
  warehouseField('cargoReleaseNumber', 'Número de liberación', 'Cargo release number', 'additionalInfo', { visible: false }),
  warehouseField('releaseDate', 'Fecha de liberación', 'Release date', 'additionalInfo', { type: 'date', visible: false }),
  warehouseField('locationDescription', 'Ubicación en bodega', 'Location description', 'additionalInfo', { visible: false })
]

/**
 * Banco de campos mock completo
 */
export const mockFieldsBank: FieldsBank = {
  'purchaseOrders': {
    general: purchaseOrdersSystemFields.filter(f => f.section === 'general'),
    logistics: purchaseOrdersSystemFields.filter(f => f.section === 'logistics'),
    dates: purchaseOrdersSystemFields.filter(f => f.section === 'dates'),
    financial: purchaseOrdersSystemFields.filter(f => f.section === 'financial'),
    custom: purchaseOrdersCustomFields
  },
  'shipments': {
    // Secciones del detalle principal (ShipmentMainInfo.vue)
    references: shipmentsSystemFields.filter(f => f.section === 'references'),
    transportConfig: shipmentsSystemFields.filter(f => f.section === 'transportConfig'),
    incoterm: shipmentsSystemFields.filter(f => f.section === 'incoterm'),
    transportDocs: shipmentsSystemFields.filter(f => f.section === 'transportDocs'),
    carriers: shipmentsSystemFields.filter(f => f.section === 'carriers'),
    // Secciones de hitos (ShipmentMilestones.vue)
    requestedDates: shipmentsSystemFields.filter(f => f.section === 'requestedDates'),
    estimatedTimes: shipmentsSystemFields.filter(f => f.section === 'estimatedTimes'),
    actualEvents: shipmentsSystemFields.filter(f => f.section === 'actualEvents'),
    // Sección para campos adicionales/personalizados
    additionalInfo: shipmentsSystemFields.filter(f => f.section === 'additionalInfo'),
    custom: shipmentsCustomFields
  },
  'quotes': {
    general: quotesSystemFields.filter(f => f.section === 'general'),
    logistics: quotesSystemFields.filter(f => f.section === 'logistics'),
    dates: quotesSystemFields.filter(f => f.section === 'dates'),
    financial: quotesSystemFields.filter(f => f.section === 'financial'),
    custom: quotesCustomFields
  },
  'warehouse': Object.fromEntries(
    warehouseSections.map(({ value }) => [value, warehouseSystemFields.filter(f => f.section === value)])
  )
}

/**
 * Presets de vistas predefinidas
 */
export const mockViewPresets: ViewPreset[] = [
  {
    id: 'preset_basic',
    name: 'Vista Básica',
    description: 'Campos esenciales para visualización rápida',
    module: 'purchaseOrders',
    fields: [
      'po_purchaseOrder',
      'po_supplierName',
      'po_status',
      'po_orderDate'
    ],
    fieldOrder: [
      'po_purchaseOrder',
      'po_supplierName',
      'po_status',
      'po_orderDate'
    ],
    isDefault: true,
    isPublic: true
  },
  {
    id: 'preset_complete',
    name: 'Vista Completa',
    description: 'Todos los campos principales del sistema',
    module: 'purchaseOrders',
    fields: [
      'po_purchaseOrder',
      'po_supplierName',
      'po_status',
      'po_company',
      'po_origin',
      'po_destination',
      'po_incoterm',
      'po_orderDate',
      'po_requestedDate',
      'po_confirmDate',
      'po_currency',
      'po_totalValue'
    ],
    fieldOrder: [
      'po_purchaseOrder',
      'po_supplierName',
      'po_status',
      'po_company',
      'po_origin',
      'po_destination',
      'po_incoterm',
      'po_orderDate',
      'po_requestedDate',
      'po_confirmDate',
      'po_currency',
      'po_totalValue'
    ],
    isDefault: false,
    isPublic: true
  },
  {
    id: 'preset_financial',
    name: 'Vista Financiera',
    description: 'Enfoque en aspectos financieros',
    module: 'purchaseOrders',
    fields: [
      'po_purchaseOrder',
      'po_supplierName',
      'po_status',
      'po_currency',
      'po_totalValue',
      'po_totalWithTax',
      'po_paymentTerms',
      'po_orderDate'
    ],
    fieldOrder: [
      'po_purchaseOrder',
      'po_supplierName',
      'po_currency',
      'po_totalValue',
      'po_totalWithTax',
      'po_paymentTerms',
      'po_status',
      'po_orderDate'
    ],
    isDefault: false,
    isPublic: true
  },
  // Presets para Shipments
  {
    id: 'preset_shipments_basic',
    name: 'Vista Básica',
    description: 'Campos esenciales para visualización rápida',
    module: 'shipments',
    fields: [
      'ship_shipmentNumber',
      'ship_status',
      'ship_customerReference',
      'ship_transportMode',
      'ship_origin',
      'ship_destination',
      'ship_eta'
    ],
    fieldOrder: [
      'ship_shipmentNumber',
      'ship_status',
      'ship_customerReference',
      'ship_transportMode',
      'ship_origin',
      'ship_destination',
      'ship_eta'
    ],
    isDefault: true,
    isPublic: true
  },
  {
    id: 'preset_shipments_tracking',
    name: 'Vista Tracking',
    description: 'Enfoque en seguimiento y documentos',
    module: 'shipments',
    fields: [
      'ship_shipmentNumber',
      'ship_status',
      'ship_origin',
      'ship_destination',
      'ship_eta',
      'ship_booking',
      'ship_mbl',
      'ship_hbl',
      'ship_isTracking',
      'ship_trackingProvider'
    ],
    fieldOrder: [
      'ship_shipmentNumber',
      'ship_status',
      'ship_booking',
      'ship_mbl',
      'ship_hbl',
      'ship_origin',
      'ship_destination',
      'ship_eta',
      'ship_isTracking',
      'ship_trackingProvider'
    ],
    isDefault: false,
    isPublic: true
  },
  {
    id: 'preset_shipments_complete',
    name: 'Vista Completa',
    description: 'Todos los campos principales del sistema',
    module: 'shipments',
    fields: [
      'ship_shipmentNumber',
      'ship_status',
      'ship_customerReference',
      'ship_transportMode',
      'ship_shipmentType',
      'ship_origin',
      'ship_destination',
      'ship_incoterm',
      'ship_vessel',
      'ship_shipmentDate',
      'ship_eta',
      'ship_booking',
      'ship_mbl'
    ],
    fieldOrder: [
      'ship_shipmentNumber',
      'ship_status',
      'ship_customerReference',
      'ship_transportMode',
      'ship_shipmentType',
      'ship_origin',
      'ship_destination',
      'ship_incoterm',
      'ship_vessel',
      'ship_shipmentDate',
      'ship_eta',
      'ship_booking',
      'ship_mbl'
    ],
    isDefault: false,
    isPublic: true
  },
  // Presets para Quotes
  {
    id: 'preset_quotes_basic',
    name: 'Vista Básica',
    description: 'Campos esenciales para visualización rápida',
    module: 'quotes',
    fields: [
      'quote_quoteNumber',
      'quote_status',
      'quote_customerName',
      'quote_quoteDate',
      'quote_validUntil',
      'quote_currency',
      'quote_totalAmount'
    ],
    fieldOrder: [
      'quote_quoteNumber',
      'quote_status',
      'quote_customerName',
      'quote_quoteDate',
      'quote_validUntil',
      'quote_currency',
      'quote_totalAmount'
    ],
    isDefault: true,
    isPublic: true
  },
  {
    id: 'preset_quotes_complete',
    name: 'Vista Completa',
    description: 'Todos los campos principales del sistema',
    module: 'quotes',
    fields: [
      'quote_quoteNumber',
      'quote_status',
      'quote_customerName',
      'quote_origin',
      'quote_destination',
      'quote_quoteDate',
      'quote_validUntil',
      'quote_currency',
      'quote_totalAmount'
    ],
    fieldOrder: [
      'quote_quoteNumber',
      'quote_status',
      'quote_customerName',
      'quote_origin',
      'quote_destination',
      'quote_quoteDate',
      'quote_validUntil',
      'quote_currency',
      'quote_totalAmount'
    ],
    isDefault: false,
    isPublic: true
  }
]

/**
 * Función helper para obtener todos los campos de un módulo
 */
export function getAllFieldsByModule(module: string): FieldDefinition[] {
  const moduleFields = mockFieldsBank[module]
  if (!moduleFields) return []

  return Object.values(moduleFields).flat().map(enrichFieldWithLabel)
}

/**
 * Función helper para obtener campos por sección
 */
export function getFieldsBySection(module: string, section: string): FieldDefinition[] {
  return (mockFieldsBank[module]?.[section] || []).map(enrichFieldWithLabel)
}

/**
 * Función helper para buscar un campo por ID
 */
export function getFieldById(fieldId: string): FieldDefinition | undefined {
  for (const module of Object.values(mockFieldsBank)) {
    for (const section of Object.values(module)) {
      const field = section.find(f => f.id === fieldId)
      if (field) return enrichFieldWithLabel(field)
    }
  }
  return undefined
}

